import { LitElement, html, css, TemplateResult } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import { unsafeHTML } from 'lit/directives/unsafe-html.js';
import assert from 'assert'

interface FormItem {
    name: string;  // Required field name for form submission
    type?: string;
    placeholder?: string;
    width: number;
	value: any;
    error?: string | null;
    before?: string;
    after?: string;
    beforeIsSvg?: boolean;
    afterIsSvg?: boolean;
	readonly?: boolean;
	required?: boolean;
	disabled?:boolean;
}

interface FormData {
  [key: string]: string;
}

interface FormSubmitDetail {
  formData: FormData;
}

interface FormSubmittedDetail {
    response: any;  // Response body from the server
    status: number;
  }

@customElement('vxc-form')
export class TForm extends LitElement {
  static override styles = css`
    :host {
      display: block;
    }

    .form-container {
      display: flex;
      flex-wrap: wrap;
      xgap: 1rem;
      xpadding: 1rem;
      gap: .5rem;
      padding: .5rem;
    }

    .form-group {
      display: flex;
      flex-direction: column;
    }

    /* Default state - mobile first */
    .form-group {
      xflex: 0 0 100% !important;
	  width: var(--width-var, 100%);
    }

    /* Desktop breakpoint */
    @media (min-width: 768px) {
      .form-group {
        flex: var(--input-width, 1) 1 0% !important;
      }
    }

    .input-group {
      position: relative;
      display: flex;
      flex-wrap: nowrap;
      align-items: stretch;
      width: 100%;
    }

    .input-group-text {
      display: flex;
      align-items: center;
      xpadding: 0.375rem 0.75rem;
      padding: 0.1rem 0.2rem;
      font-size: 1rem;
      font-weight: 400;
      line-height: 1.5;
      color: #212529;
      text-align: center;
      white-space: nowrap;
      background-color: #e9ecef;
      border: 1px solid #ced4da;
      -webkit-border-radius: 5px 0 0 5px;
      border-radius: 5px 0 0 5px;
    }

    .input-group-text svg {
      width: 16px;
      height: 16px;
      display: block;
    }

    .input-group > :first-child {
      border-top-right-radius: 0;
      border-bottom-right-radius: 0;
    }

    .input-group > :last-child {
      border-top-left-radius: 0;
      border-bottom-left-radius: 0;
    }

    .input-group > :not(:first-child):not(:last-child) {
      border-radius: 0;
    }

    input {
      xpadding: 0.5rem;
      padding: 0;
      border: 1px solid #ced4da;
      border-radius: 5px;
      font-size: 1rem;
      line-height: 1.5;
      width: 100%;
      box-sizing: border-box;
      transition: border-color 0.15s ease-in-out, box-shadow 0.15s ease-in-out;
      position: relative;
      flex: 1 1 auto;
      min-width: 0;
    }

    input:focus {
      border-color: #86b7fe;
      outline: 0;
      box-shadow: 0 0 0 0.25rem rgba(13, 110, 253, 0.25);
    }

    /* Error state styling */
    input.error {
      border-color: #dc3545;
      padding-right: calc(1.5em + 0.75rem);
      background-repeat: no-repeat;
      background-position: right calc(0.375em + 0.1875rem) center;
      background-size: calc(0.75em + 0.375rem) calc(0.75em + 0.375rem);
    }

    input.error:focus {
      border-color: #dc3545;
      box-shadow: 0 0 0 0.25rem rgba(220, 53, 69, 0.25);
    }

    .error-message {
      color: #dc3545;
      font-size: 0.875em;
      margin-top: 0.25rem;
    }

    button {
      xpadding: 0.5rem 1rem;
      padding: 0;
      font-size: 1rem;
      line-height: 1.5;
      border-radius: 0.25rem;
      color: #fff;
      background-color:rgb(116, 116, 117);
      border: 1px solidrgb(37, 37, 37);
      cursor: pointer;
      transition: background-color 0.15s ease-in-out;
      width: 100%;
    }

    button:hover {
      padding: 0.375rem 0.75rem;
    }

    /* Desktop styles for submit button */
    @media (min-width: 768px) {
      .submit-container {
        flex: 0 0 auto !important;
      }

      input {
        padding: 0.5rem;
      }

      .input-group-text {
        display: flex;
        align-items: center;
        padding: 0.375rem 0.75rem;
      }

      button {
        width: auto;
        padding: 0.5rem 1rem;
      }
    }
  `;

    @property({ type: Array, attribute: 'form-items' })items: FormItem[] = [];
    @property({ type: String, attribute: 'form-data' }) formData = '';
    @property({ type: String, attribute: 'submit-btn-text' }) submitText = 'submit';
    @property({ type: String, attribute: 'submit-btn-color' }) submitColor = 'primary';
    @property({ type: String })action = '';  // URL to submit the form to
    @property({ type: String }) method: 'GET' | 'POST' | 'PUT' | 'PATCH' | 'DELETE' = 'POST';  // HTTP method
  
    private _formData: FormData = {};

    private _handleInput(name: string, event: Event): void {
        const target = event.target as HTMLInputElement;
        if(name)	this._formData[name] = target.value;
    }
    
    private async _handleSubmit(event: Event): Promise<void> {
      event.preventDefault();
		  try {
        assert(this.action, 'form action URL is required')
        if(this.action.startsWith('session:')) {
          let varname: string = this.action.split(':')[1]
          console.log('save to session to', varname)
          assert(varname, 'session variable name is required')
          var payload: { [key: string]: any } = this._formData
          for(const [key, value] of Object.entries(payload)) {
            console.log('storing', key, value)
            sessionStorage.setItem(`${varname}.${key}`, value)
          }
        console.log('session.storage =', Object.fromEntries(Object.entries(sessionStorage)));
        this.dispatchEvent(new CustomEvent(`${varname}.set`, {
          detail: payload,
          bubbles: true,
          composed: true
        }));
      }
        else return this._submit()
		}
		catch(e) { console.error(e) }
	}

	private async _submit(): Promise<void> {
      	try {
        // Prepare request options
        const options: RequestInit = {
          method: this.method,
          headers: {
            'Content-Type': 'application/json',
          },
        };
  
        // Add body for non-GET requests
        if (this.method !== 'GET') {
          options.body = JSON.stringify(this._formData);
        }
  
        // Make the request
        const response = await fetch(this.action, options);
        
        // Check if response is successful (status in 200-299 range)
        if (response.ok) {
          const responseData = await response.json();
          
          // Dispatch success event with response data
          const detail: FormSubmittedDetail = {
            response: responseData,
            status: response.status
          };
          
          this.dispatchEvent(new CustomEvent<FormSubmittedDetail>('submitted', { 
            detail,
            bubbles: true,
            composed: true
          }));
  
          // Also dispatch the original form-submit event
          const formDetail: FormSubmitDetail = {
            formData: this._formData
          };
          this.dispatchEvent(new CustomEvent<FormSubmitDetail>('form-submit', { 
            detail: formDetail,
            bubbles: true,
            composed: true
          }));
        } else {
          throw new Error(`HTTP error! status: ${response.status}`);
        }
      } catch (error) {
        console.error('Error submitting form:', error);
        // Dispatch error event
        this.dispatchEvent(new CustomEvent('submit-error', { 
          detail: { error },
          bubbles: true,
          composed: true
        }));
      }
    }
  
    private renderAddon(content?: string): TemplateResult {
		var resp = html``
		try {
			assert(content?.length, 'no content')
			var isHtml: boolean = /<\/?[a-z][\s\S]*>/i.test(content)
			console.log('renderAddon', content, 'isHtml = ', isHtml)
			resp = isHtml ? 
				html`<span class="input-group-text">${unsafeHTML(content)}</span>` :
				html`<span class="input-group-text">${content}</span>`
			}
		catch(e) {}
		finally  { return resp }
    }
  
    protected override render(): TemplateResult {
		console.log('render items', this.items)
		for(let input of this.items) {
			if(input.value && input.name)
				this._formData[input.name] = input.value
		}
      	return html`
			<form @submit=${this._handleSubmit} method=${this.method}>
				<div class="form-container">
					${this.items.map((item: FormItem) => {
					const width: number = item.width === 100 ? 100 : Math.min(item.width||100, 99);
					return html`
						<div class="form-group" style="--input-width: ${width||100}%;">
						<div class="input-group">
							${this.renderAddon(item.before)}
							<input
							name=${item.name}
							type=${item.type || 'text'}
							placeholder=${item.placeholder || ''}
							@input=${(e: Event) => this._handleInput(item.name, e)}
							class=${item.error ? 'error' : ''}
							value=${item.value}
							?readonly="${item.readonly}"
							?required="${item.required}"
							?disabled="${item.disabled}"
							/>
							${this.renderAddon(item.after)}
						</div>
						${item.error ? html`
							<div class="error-message">${item.error}</div>
						` : ''}
						</div>
					`;
					})}
					<div class="form-group submit-container">
					<button type="submit">${this.submitText}</button>
					</div>
				</div>
			</form>
      `;
    }
}

declare global {
  interface HTMLElementTagNameMap {
    'vxc-form': TForm;
  }
}